import { VariableType } from 'json-to-graphql-query';
import { StateString } from '@witkac/kartamiejska-shared';
import { EntityResource } from './entity.resource';
import { BenefitVariables, BenefitArgs } from './queryVariables/benefit';
export class BenefitResource extends EntityResource {
    constructor() {
        super({
            entityName: 'benefit',
            addMutationName: 'benefitAdd'
        });
        this.getFields = {
            id: true,
            name: true,
            value: true,
            type: true,
            groups: { id: true, name: true },
            categories: { id: true, name: true, state: true },
            description: true,
            descriptionShort: true,
            userPoints: true,
            start: true,
            end: true,
            max: true,
            maxPerUser: true,
            maxPerUserPeriod: true,
            images: true,
            state: true,
            mainImage: true,
            place: {
                id: true,
                name: true,
                street: true,
                localNumber: true,
                homeNumber: true,
                city: true,
                postalCode: true
            },
            older: { id: true },
            newer: { id: true },
            partner: { id: true, name: true, phoneNumber: true, website: true, email: true, mainImage: true },
            partners: { id: true, name: true, phoneNumber: true, website: true, email: true, mainImage: true },
            pointCost: true,
            containerBenefit: {
                id: true,
                pointContainer: {
                    id: true,
                    name: true
                },
                points: true,
                maxUsagesPerUser: true
            },
            pointContainer: { id: true, name: true },
            defaultPrice: true,
            defaultAmount: true,
            shared: true,
            waitTime: true
        };
        this.getFieldsWithProfileId = {
            ...this.getFields,
            userPoints: { __args: { filters: new VariableType('filters') } }
        };
        this.listFields = {
            id: true,
            name: true,
            created: true,
            value: true,
            shared: true,
            type: true,
            start: true,
            end: true,
            mainImage: true,
            state: true,
            older: { id: true, state: true },
            newer: { id: true, state: true },
            groups: { id: true, name: true },
            categories: { id: true, name: true, state: true },
            place: {
                id: true,
                name: true,
                street: true,
                localNumber: true,
                homeNumber: true,
                city: true,
                postalCode: true
            },
            partner: { id: true, name: true, mainImage: true },
            partners: { id: true, name: true },
            descriptionShort: true,
            defaultPrice: true,
            pointContainer: { id: true, name: true },
            pointCost: true,
            userPoints: { __args: { filters: new VariableType('filters') } },
            containerBenefit: {
                id: true,
                pointContainer: {
                    id: true,
                    name: true
                },
                points: true
            }
        };
        this.addVariables = { partnerId: 'Int!', ...BenefitVariables };
        this.addArgs = {
            partnerId: new VariableType('partnerId'),
            ...BenefitArgs
        };
        this.updateVariables = { id: 'Int!', ...BenefitVariables };
        this.updateArgs = { id: new VariableType('id'), ...BenefitArgs };
        this.listVariables = { filters: 'BenefitFilters' };
        this.addSharedVariables = { partnerIds: '[Int!]', ...BenefitVariables };
        this.addSharedArgs = {
            partnerIds: new VariableType('partnerIds'),
            ...BenefitArgs
        };
    }
    /**
     * Wczytywanie listy dostępnych usług u danego partnera dla posiadacza karty
     *
     * @param {BenefitFilter} [filters={}]
     * @returns {Promise<ListResponse<Benefit>>}
     * @memberof BenefitResource
     */
    async availableBenefits(filters = {}) {
        const query = this.prepareOptions({
            query: {
                __variables: { ...this.listVariables },
                benefits: {
                    __args: { ...this.listArgs },
                    count: true,
                    list: {
                        ...this.listFields
                    }
                }
            }
        });
        return this.query({ query, variables: { filters: { ...filters, state: StateString.ACTIVE, upToDate: true } } }, 'benefits');
    }
    /**
     * Wczytanie listy dostępnych usług dla zalogowanego użytkownika
     *
     * @param {BenefitFilter} [filters={}]
     * @param {boolean} [preventLoader=false]
     * @param {*} [fields=this.listFields]
     * @returns {Promise<ListResponse<Benefit>>}
     * @memberof BenefitResource
     */
    async userBenefits(filters = {}, preventLoader = false, fields = this.listFields) {
        const query = this.prepareOptions({
            query: {
                __variables: { ...this.listVariables },
                benefitsAvailable: {
                    __args: { ...this.listArgs },
                    count: true,
                    list: {
                        ...fields
                    }
                }
            }
        });
        return this.query({ query, variables: { filters } }, 'benefitsAvailable', preventLoader);
    }
    /**
     * Akceptacja usługi
     *
     * @param {number} id
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    async confirm(id) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!' },
                benefitConfirm: {
                    __args: {
                        id: new VariableType('id')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id } }, 'benefitConfirm');
    }
    /**
     * Dodawanie usługi współdzielonej
     *
     * @param {*} variables
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    async addShared(variables) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: this.addSharedVariables,
                sharedBenefitAdd: {
                    __args: this.addSharedArgs
                }
            }
        });
        return this.mutation({ mutation, variables }, 'sharedBenefitAdd');
    }
    /**
     * Aktualizacja usługi współdzielonej
     *
     * @param {number} id
     * @param {*} data
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    updateShared(id, data) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: this.updateVariables,
                sharedBenefitUpdate: { __args: this.updateArgs }
            }
        });
        return this.mutation({ mutation, variables: { ...data, id } }, 'sharedBenefitUpdate');
    }
    /**
     * Usunięcie współdzielonej usługi
     *
     * @param {number} id
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    deleteShared(id) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!' },
                sharedBenefitDelete: {
                    __args: {
                        id: new VariableType('id')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id } }, 'sharedBenefitDelete');
    }
    /**
     * Dodanie wielu partnerów do usługi współdzielonej
     *
     * @param {number} id
     * @param {number[]} partnerIds
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    assignPartners(id, partnerIds) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!', partnerIds: '[Int!]' },
                benefitAssign: {
                    __args: {
                        id: new VariableType('id'),
                        partnerIds: new VariableType('partnerIds')
                    },
                    partners: {
                        id: true,
                        name: true
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id, partnerIds } }, 'benefitAssign');
    }
    /**
     * Usunięcie przypisania partnera do usługi współdzielonej
     *
     * @param {number} id
     * @param {number} partnerId
     * @returns {Promise<number>}
     * @memberof BenefitResource
     */
    unassignPartner(id, partnerId) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!', partnerId: 'Int!' },
                benefitUnassignPartner: {
                    __args: {
                        id: new VariableType('id'),
                        partnerId: new VariableType('partnerId')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id, partnerId } }, 'benefitUnassignPartner');
    }
    /**
     * Pobranie benefitu z informacjami o podanym użytkowniku
     *
     * @param {({ id: number | string; profileId: number })} variables
     * @param {boolean} [preventLoader=false]
     * @param {*} [fields=this.getFieldsWithProfileId]
     * @return {Promise<Benefit>}
     * @memberof BenefitResource
     */
    getWithProfileId(variables, preventLoader = false, fields = this.getFieldsWithProfileId) {
        const query = this.prepareOptions({
            query: {
                __variables: { id: 'Int!', filters: 'BenefitFilters' },
                benefit: {
                    __args: this.getArgs,
                    ...fields
                }
            }
        });
        return this.query({ query, variables: { id: variables.id, filters: { profileId: variables.profileId } } }, 'benefit', preventLoader);
    }
}
export const benefitResource = new BenefitResource();
//# sourceMappingURL=benefit.resource.js.map